import {
  PaletteColorOptions,
  PaletteMode,
  PaletteOptions,
  ThemeOptions,
} from '@mui/material';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';

declare module '@mui/material/styles' {
  interface Palette {
    clientColor: PaletteColorOptions;
    pwcUserColor: PaletteColorOptions;
    thirdPartyColor: PaletteColorOptions;
    disabledLink: PaletteColorOptions;
  }
  interface PaletteOptions {
    clientColor: PaletteColorOptions;
    pwcUserColor: PaletteColorOptions;
    thirdPartyColor: PaletteColorOptions;
    disabledLink: PaletteColorOptions;
  }
}

const customPalette: { dark: PaletteOptions; light: PaletteOptions } = {
  light: {
    primary: {
      main: '#bd4301', // darker orange
    },
    error: {
      main: '#C52A1A', // red
    },
    text: {
      secondary: '#bbbbbb', // grey
    },
    divider: '#bbb', // light grey,
    clientColor: {
      main: '#FFB600', // dark yellow
    },
    pwcUserColor: {
      main: '#D93954', // pink-red
    },
    thirdPartyColor: {
      main: '#0089EB', // blue
    },
    disabledLink: {
      main: '#bbb', // grey
    },
  },
  dark: {
    primary: {
      main: '#D04A02', // orange
    },
    success: {
      main: '#1E8629', // green
    },
    error: {
      main: '#C52A1A', // red
    },
    divider: '#464646', // dark grey
    text: {
      secondary: '#bbbbbb', // grey
    },
    background: {
      paper: '#1E1E1E', // grey
    },
    clientColor: {
      main: '#FFB600', // dark yellow
    },
    pwcUserColor: {
      main: '#D93954', // pink-red
    },
    thirdPartyColor: {
      main: '#0089EB', // blue
    },
    disabledLink: {
      main: '#464646', // grey
    },
  },
};

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false; // 0 // removes the `xs` breakpoint
    sm: false; // 600
    md: false; // 900
    lg: false; // 1200
    xl: false; // 1536
    viewportXS: true; // 0 // adds the `viewportXS` breakpoint
    viewportS: true; // 577
    viewportM: true; // 768
    viewportL: true; // 992
    viewportXL: true; // 1200
  }
}

export const getMuiTheme = (mode: PaletteMode): ThemeOptions => ({
  breakpoints: {
    values: {
      viewportXS: 0,
      viewportS: 577,
      viewportM: 768,
      viewportL: 992,
      viewportXL: 1200,
    },
  },
  typography: {
    fontFamily: 'var(--font-family)',
  },
  palette: {
    mode,
    ...customPalette[mode],
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '9px',
          height: '1.25rem',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingRight: 0,
          paddingLeft: 0,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          padding: '1.5rem',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '2px',
        },
        outlined: ({ theme }) => ({
          borderRadius: '2px',
          borderColor: theme.palette.primary.main,
          '&:hover': {
            textDecoration: 'underline',
            backgroundColor: 'transparent',
          },
        }),
        sizeLarge: {
          fontSize: '1rem',
          padding: '0.75rem 1.5rem',
          lineHeight: '1.5rem',
        },
        text: {
          textDecoration: 'underline',
          '&:hover': {
            textDecoration: 'none',
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: theme.palette.mode === 'dark' ? '#464646' : '#dedede',
            background:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.176)'
                : 'rgba(0, 0, 0, 0.05)',
          },
        }),
      },
    },
  },
});
